import {useMergeboxStatusCalculator} from '@github-ui/mergebox/use-mergebox-status-calculator'
import {EMPTY_CHANNELS} from '@github-ui/mergebox/use-mergeability-live-updates'
import {useLayoutRouteData} from '../../../../page-data/loaders/use-layout-route-data'

export type MergeBoxPullRequest = ReturnType<typeof useMergeboxStatusCalculator>['pullRequest']

/**
 * Wraps the merge-status calculator pipeline (which also wires up the relevant
 * live updates) and exposes the mergeability data that For You items derive their state from.
 *
 * Reads merge data straight from the layout payload rather than pre-validating
 * it against the merge-status-button rules (open state, feature-flag opt-out).
 * Those rules exclude closed/merged pull requests, which some For You items still need.
 * Each item decides its own visibility from the relevant isClosedOrMerged state.
 *
 * Returns `null` only when the layout has no merge data at all, in which case
 * consumers should early return so the item is not registered as visible.
 */
export function useMergeabilityData() {
  const {mergeStatusButtonData} = useLayoutRouteData()

  // hack: the calculator hook was not built with undefined/null in mind
  const headSha = mergeStatusButtonData?.headSha ?? ''
  const channels = mergeStatusButtonData?.channels ?? EMPTY_CHANNELS

  const {
    calculator,
    pullRequest: mergeBoxPullRequest,
    mergeRequirements,
    statusChecksData,
  } = useMergeboxStatusCalculator({headSha, channels})

  if (!mergeStatusButtonData) return null

  const userStateCondition = mergeRequirements?.conditions.find(
    condition => condition.type === 'PULL_REQUEST_USER_STATE',
  )
  const canUserPushToBase = userStateCondition?.result === 'PASSED'

  return {
    mergeBoxPullRequest,
    mergeRequirements,
    statusChecksData,
    statusChecks: statusChecksData.statusChecks,
    statusRollup: statusChecksData.statusRollup,
    checksSection: calculator.sections.ChecksSection,
    conflictsSection: calculator.sections.ConflictsSection,
    blockedSection: calculator.sections.BlockedSection,
    reviewerSection: calculator.sections.ReviewerSection,
    canUserPushToBase,
    isClosedOrMerged: calculator.sections.ClosedOrMergedStateMergeBox.shouldRender,
  }
}

export type MergeabilityData = NonNullable<ReturnType<typeof useMergeabilityData>>
